import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  ["Install GlossLink", "Free on the Chrome Web Store."],
  ["Pick your textbook", "Preview the source and license, then click Add to my GlossLink."],
  ["Read anywhere", "Every glossary term lights up on Canvas, PDFs and articles — hover for the textbook's exact definition."],
];

export const GlossPacksSection = () => {
  const [packs, setPacks] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/packs`).then((r) => (r.ok ? r.json() : [])).then(setPacks).catch(() => {});
  }, []);
  const terms = packs.reduce((n, p) => n + p.entry_count, 0);
  const featured = packs.slice(0, 4);

  return (
    <section id="packs" className="bg-white border-y border-slate-200 py-20" data-testid="home-packs-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full mb-5">GLOSS PACKS · INCLUDED WITH GLOSSLINK</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">Your textbook's whole glossary. <span className="text-blue-600">One click.</span></h2>
          <p className="mt-4 text-lg text-slate-600">No typing, no uploading. Pick your textbook and every term in it is highlighted on everything you read, with the book's exact definition on hover — word for word, with the source credited.</p>
          {packs.length > 0 && (
            <p className="mt-3 text-sm font-semibold text-slate-700" data-testid="home-packs-stats">
              {packs.length} packs · {terms.toLocaleString()} terms · every pack verified against its official glossary page
            </p>
          )}
        </div>

        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {STEPS.map(([title, body], i) => (
            <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-2xl p-6" data-testid={`home-packs-step-${i}`}>
              <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center shadow-lg shadow-blue-600/30">{i + 1}</div>
              <h3 className="font-bold text-lg mb-2 mt-3">{title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {featured.length > 0 && (
          <div className="mt-14">
            <div className="flex items-end justify-between gap-4 mb-5">
              <div>
                <h3 className="text-xl font-extrabold">Built for nursing students first</h3>
                <p className="text-sm text-slate-600">The full Open RN textbook line — Fundamentals, Skills, Pharmacology, Mental Health, Med Term and more. CC BY 4.0.</p>
              </div>
              <Link to="/for-nurses" className="hidden sm:inline text-sm font-semibold text-blue-700 hover:underline" data-testid="home-packs-see-all">Nursing students: start here →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.map((p) => (
                <Link key={p.id} to={`/packs/${p.id}`} className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition" data-testid={`home-pack-${p.id}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{p.subject} · {p.edition}</div>
                  <div className="mt-2 font-extrabold leading-snug text-slate-900 group-hover:text-blue-700">{p.title.split(" — ")[0]}</div>
                  <div className="mt-4 text-3xl font-extrabold">{p.entry_count.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">terms, word for word</div>
                  {p.verification?.verified && <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" />Verified against source</div>}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 text-center">
          <Link to="/packs" className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-blue-600/20" data-testid="home-packs-cta">Browse Gloss Packs →</Link>
          <div className="mt-4 text-xs text-slate-500">No account needed · 20-day free trial of everything · then 20 most-used terms free forever + quizzes & flashcards · Pro ($9.99/mo or $75/yr) unlocks every pack, every word · Don't see your textbook? Request it on the Gloss Packs page</div>
        </div>
      </div>
    </section>
  );
};
